import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchTechServicesSelect } from '../services/techServicesService';
import { fetchTeknikServisStokV2 } from '../services/stokV2Service';
import { createParcaGonderim } from '../services/parcaGonderimService';
import StokLimitliParcaPicker from './parcaSecim/StokLimitliParcaPicker';
import IntegerInput from './IntegerInput';
import ParcaPickerNestedModal from './ParcaPickerNestedModal';
import { parseAdet } from '../utils/integerInput';
import './SiparisOlusturModal.css';

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const GonderimOlusturModal = ({ onClose, onSuccess }) => {
  const [techServices, setTechServices] = useState([]);
  const [gonderenId, setGonderenId] = useState('');
  const [aliciId, setAliciId] = useState('');
  const [not, setNot] = useState('');
  const [katalog, setKatalog] = useState([]);
  const [secilenParcalar, setSecilenParcalar] = useState([]);
  const [loadingTs, setLoadingTs] = useState(true);
  const [loadingStok, setLoadingStok] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [parcaPickerOpen, setParcaPickerOpen] = useState(false);

  useEffect(() => {
    (async () => {
      setLoadingTs(true);
      try {
        const res = await fetchTechServicesSelect();
        const list = res?.veri?.teknikServisler || res?.veri || [];
        setTechServices(Array.isArray(list) ? list : []);
      } catch {
        setTechServices([]);
      } finally {
        setLoadingTs(false);
      }
    })();
  }, []);

  const loadGonderenStok = useCallback(async (tsId) => {
    if (!tsId) {
      setKatalog([]);
      setSecilenParcalar([]);
      return;
    }
    setLoadingStok(true);
    try {
      const res = await fetchTeknikServisStokV2(tsId, { sayfa: 1, limit: 500, tab: 'guncel' });
      const rows = res?.veri?.stokListesi || [];
      setKatalog(
        rows.map((r) => ({
          icerikId: r.icerikId,
          parcaAdi: r.parcaAdi,
          parcaKodu: r.parcaKodu,
          kategoriId: r.kategoriId,
          stokMusait: r.adet ?? 0,
          ozAdet: r.ozAdet ?? 0,
          konsinyeAdet: r.konsinyeAdet ?? 0,
          kullanilabilir: r.adet ?? 0,
          fiyatgaranti: null,
        })),
      );
      setSecilenParcalar([]);
    } catch {
      setKatalog([]);
    } finally {
      setLoadingStok(false);
    }
  }, []);

  useEffect(() => {
    loadGonderenStok(gonderenId);
  }, [gonderenId, loadGonderenStok]);

  const aliciOptions = useMemo(
    () => techServices.filter((ts) => String(ts.id || ts._id) !== String(gonderenId)),
    [techServices, gonderenId],
  );

  const handleSubmit = async () => {
    setError('');
    if (!gonderenId) {
      setError('Gönderen teknik servis seçin');
      return;
    }
    if (!aliciId) {
      setError('Alıcı teknik servis seçin');
      return;
    }
    if (secilenParcalar.length === 0) {
      setError('En az bir parça seçin');
      return;
    }
    setSubmitting(true);
    try {
      const pozisyonlar = secilenParcalar.map((p) => ({
        icerikId: p.icerikId,
        adet: p.adet || 1,
      }));
      const res = await createParcaGonderim({
        gonderenTeknikServisId: gonderenId,
        aliciTeknikServisId: aliciId,
        pozisyonlar,
        not: not.trim() || undefined,
      });
      if (res?.basarili) {
        window.showNotification?.('success', res?.mesaj || 'Gönderim oluşturuldu');
        onSuccess?.();
        onClose();
      } else {
        setError(res?.mesaj || 'Gönderim oluşturulamadı');
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Gönderim oluşturulamadı'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay siparis-modal siparis-modal--parts" onClick={onClose} role="presentation">
      <div className="modal-content siparis-modal__content--wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Gönderim Oluştur</h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Kapat">
            <i className="fas fa-times" />
          </button>
        </div>
        <div className="modal-body siparis-modal__body--picker">
          <div className="gonderim-olustur-ts-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div className="form-group">
              <label style={{ fontWeight: 500, marginBottom: 6, display: 'block' }}>Gönderen teknik servis</label>
              <select
                value={gonderenId}
                onChange={(e) => {
                  setGonderenId(e.target.value);
                  if (aliciId === e.target.value) setAliciId('');
                }}
                disabled={loadingTs || submitting}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border-color, #dee2e6)' }}
              >
                <option value="">Seçin...</option>
                {techServices.map((ts) => {
                  const id = ts.id || ts._id;
                  return (
                    <option key={id} value={id}>
                      {ts.ad || ts.kod || id}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="form-group">
              <label style={{ fontWeight: 500, marginBottom: 6, display: 'block' }}>Alıcı teknik servis</label>
              <select
                value={aliciId}
                onChange={(e) => setAliciId(e.target.value)}
                disabled={loadingTs || submitting || !gonderenId}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border-color, #dee2e6)' }}
              >
                <option value="">Seçin...</option>
                {aliciOptions.map((ts) => {
                  const id = ts.id || ts._id;
                  return (
                    <option key={id} value={id}>
                      {ts.ad || ts.kod || id}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: 16 }}>
            <label style={{ fontWeight: 500, marginBottom: 6, display: 'block' }}>Not (opsiyonel)</label>
            <textarea
              value={not}
              onChange={(e) => setNot(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Gönderim notu..."
              style={{ width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border-color, #dee2e6)', resize: 'vertical' }}
            />
          </div>

          {!gonderenId ? (
            <p className="siparis-modal-step-desc">Parça seçmek için önce gönderen teknik servisi seçin.</p>
          ) : (
            <>
              <div className="parca-sec-open-picker-row">
                <button
                  type="button"
                  className="btn btn-secondary parca-sec-open-picker"
                  onClick={() => setParcaPickerOpen(true)}
                  disabled={loadingStok}
                >
                  <i className="fas fa-layer-group" aria-hidden /> Parça Seç
                  {secilenParcalar.length > 0 ? (
                    <span className="parca-picker-selected-badge-count" style={{ marginLeft: 4 }}>
                      ({secilenParcalar.length})
                    </span>
                  ) : null}
                </button>
              </div>
              <div className="parca-sec-table-wrap">
                {secilenParcalar.length === 0 ? (
                  <div className="parca-sec-empty-positions">
                    Henüz parça eklenmedi. «Parça Seç» ile gönderilecek parçaları ekleyin.
                  </div>
                ) : (
                  <table className="admins-table">
                    <thead>
                      <tr>
                        <th>Parça</th>
                        <th style={{ textAlign: 'center' }}>Adet</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {secilenParcalar.map((p) => {
                        const kat = katalog.find((k) => String(k.icerikId) === String(p.icerikId));
                        const max = kat?.kullanilabilir ?? 999;
                        return (
                          <tr key={p.icerikId}>
                            <td>{p.parcaAdi}</td>
                            <td style={{ textAlign: 'center' }}>
                              <IntegerInput
                                min={1}
                                max={max}
                                className="parca-sec-qty-input"
                                value={p.adet || 1}
                                onChange={(v) => {
                                  const num = parseAdet(v, { min: 1, max });
                                  setSecilenParcalar((prev) =>
                                    prev.map((x) =>
                                      String(x.icerikId) === String(p.icerikId)
                                        ? { ...x, adet: num }
                                        : x,
                                    ),
                                  );
                                }}
                              />
                            </td>
                            <td>
                              <button
                                type="button"
                                className="category-action-btn delete-btn"
                                onClick={() =>
                                  setSecilenParcalar((prev) =>
                                    prev.filter((x) => String(x.icerikId) !== String(p.icerikId)),
                                  )
                                }
                              >
                                <i className="fas fa-trash-alt" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
              <ParcaPickerNestedModal
                open={parcaPickerOpen}
                onClose={() => setParcaPickerOpen(false)}
                title="Parça seç — gönderen stok"
                selectedCount={secilenParcalar.length}
              >
                <StokLimitliParcaPicker
                  katalog={katalog}
                  loading={loadingStok}
                  secilenParcalar={secilenParcalar}
                  onSecilenChange={setSecilenParcalar}
                  showSelectedSummary={false}
                />
              </ParcaPickerNestedModal>
            </>
          )}

          {error && (
            <div style={{ marginTop: 12, color: 'var(--danger)', fontSize: 13 }}>{error}</div>
          )}

          <div className="siparis-modal-footer-btns" style={{ marginTop: 16 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
              İptal
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={submitting || !gonderenId || !aliciId || secilenParcalar.length === 0}
              onClick={handleSubmit}
            >
              {submitting ? (
                <>
                  <i className="fas fa-spinner fa-spin" style={{ marginRight: 8 }} />
                  Oluşturuluyor...
                </>
              ) : (
                'Gönderim Oluştur'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GonderimOlusturModal;
